import React, { Component } from "react";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import AccountCircle from "@material-ui/icons/AccountCircle";
import Assignment from "@material-ui/icons/Assignment";
import AccountBalance from "@material-ui/icons/AccountBalance";
import School from "@material-ui/icons/School";
import EventIcon from "@material-ui/icons/Event";
import WarningIcon from "@material-ui/icons/Warning";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardBody from "components/Card/CardBody.jsx";

import api from "api/index";

const NAVY = "#1a237e";

const styles = {
  heroWrap: {
    position: "relative",
    width: "100%",
    height: "220px",
    borderRadius: "16px",
    overflow: "hidden",
    marginBottom: "30px",
    background: `linear-gradient(135deg, ${NAVY} 0%, #311b92 100%)`,
    boxShadow: "0 10px 30px -12px rgba(26, 35, 126, 0.42)"
  },
  heroContent: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    padding: "40px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    color: "#fff"
  },
  heroTitle: { fontSize: "28px", fontWeight: 800, margin: 0, letterSpacing: "-0.5px" },
  heroSub: { fontSize: "15px", margin: "8px 0 0", opacity: 0.85, fontWeight: 400 },
  
  statCard: { 
    padding: "24px", 
    borderRadius: "16px", 
    background: "#fff", 
    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
    display: "flex",
    alignItems: "center",
    gap: "20px",
    transition: "transform 0.2s ease",
    "&:hover": { transform: "translateY(-4px)" }
  },
  iconBox: {
    width: "56px",
    height: "56px",
    borderRadius: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  statNum: { fontSize: "22px", fontWeight: 800, color: "#1e293b", margin: 0 },
  statLabel: { fontSize: "13px", color: "#64748b", margin: "2px 0 0", fontWeight: 500 },

  datesCard: { padding: "8px 24px", borderRadius: "16px"},
  datesTitle: { fontSize: "15px", fontWeight: 700, color: "#1e293b", margin: "16px 0" },
  dateRow: {
    display: "flex", alignItems: "center", gap: "16px",
    padding: "12px 0", borderBottom: "1px solid #f1f5f9"
  },
  dateBadge: {
    width: "44px", height: "44px", borderRadius: "12px",
    background: "#e8eaf6", color: NAVY,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
  },
  dateEventTitle: { fontSize: "14px", fontWeight: 600, color: "#1e293b", margin: 0 },
  dateEventWhen: { fontSize: "12px", color: "#64748b", margin: "2px 0 0" }
};

const EVENT_TYPE_LABELS = {
  enrollment_open: "Enrollment Opens",
  enrollment_close: "Enrollment Closes",
  fee_deadline: "Fee Deadline",
  exam_start: "Exams Start",
  exam_end: "Exams End",
  results_release: "Results Release",
  other: "Important Date"
};

class PortalDashboardInner extends Component {
  state = { profile: null, feesSummary: null, semesters: [], enrollments: [], calendar: [], retakes: [], loading: true };

  componentDidMount() {
    this.load();
  }

  load = async () => {
    try {
      const [profileRes, feesRes, semestersRes, calendarRes, retakesRes] = await Promise.all([
        api.get("/portal/profile"),
        api.get("/portal/fees"),
        api.get("/portal/semesters"),
        api.get("/portal/calendar").catch(() => ({ data: { data: [] } })),
        api.get("/portal/retakes").catch(() => ({ data: { data: [] } }))
      ]);
      const profile = profileRes.data.data || profileRes.data;
      const feesSummary = (feesRes.data.data || feesRes.data).summary;
      const semesters = semestersRes.data.data || semestersRes.data;
      const calendar = calendarRes.data.data || calendarRes.data;
      const retakes = retakesRes.data.data || retakesRes.data;
      const current = semesters.find(s => s.isCurrent);

      let enrollments = [];
      if (current) {
        const enrRes = await api.get("/portal/enrollments", { params: { semesterId: current.id } });
        enrollments = (enrRes.data.data || enrRes.data).filter(e => e.status === "enrolled");
      }

      this.setState({
        profile, feesSummary, semesters, enrollments,
        calendar: Array.isArray(calendar) ? calendar : [],
        retakes: Array.isArray(retakes) ? retakes : [],
        loading: false
      });
    } catch (e) {
      this.setState({ loading: false });
    }
  };

  render() {
    const { classes } = this.props;
    const { profile, feesSummary, enrollments, calendar, retakes, loading } = this.state;

    if (loading) return <div style={{ textAlign: "center", padding: "100px" }}><CircularProgress /></div>;

    const totalCredits = enrollments.reduce((sum, e) => sum + (e.course?.creditUnits || 0), 0);
    const balance = feesSummary?.balance || 0;
    const upcomingDates = calendar
      .filter(ev => !ev.date || new Date(ev.date) >= new Date(new Date().toDateString()))
      .slice(0, 5);

    return (
      <div style={{ padding: "10px" }}>
        <div className={classes.heroWrap}>
          <div className={classes.heroContent}>
            <h1 className={classes.heroTitle}>Welcome back, {profile?.firstName}!</h1>
            <p className={classes.heroSub}>
              {profile?.studentNo} • {profile?.programme?.name || "Student Portal"}
            </p>
          </div>
        </div>

        <GridContainer>
          <GridItem xs={12} sm={6} md={4}>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "#e0f2fe", color: "#0369a1" }}>
                <School />
              </div>
              <div>
                <p className={classes.statNum}>{enrollments.length}</p>
                <p className={classes.statLabel}>Active Courses</p>
              </div>
            </div>
          </GridItem>
          
          <GridItem xs={12} sm={6} md={4}>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "#f0fdf4", color: "#15803d" }}>
                <Assignment />
              </div>
              <div>
                <p className={classes.statNum}>{totalCredits}</p>
                <p className={classes.statLabel}>Credit Units</p>
              </div>
            </div>
          </GridItem>

          <GridItem xs={12} sm={6} md={4}>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: balance > 0 ? "#fef2f2" : "#f0fdf4", color: balance > 0 ? "#b91c1c" : "#15803d" }}>
                <AccountBalance />
              </div>
              <div>
                <p className={classes.statNum}>UGX {balance.toLocaleString()}</p>
                <p className={classes.statLabel}>Fees Balance</p>
              </div>
            </div>
          </GridItem>

          {retakes.length > 0 && (
            <GridItem xs={12} sm={6} md={4}>
              <div className={classes.statCard}>
                <div className={classes.iconBox} style={{ background: "#fef2f2", color: "#b91c1c" }}>
                  <WarningIcon />
                </div>
                <div>
                  <p className={classes.statNum}>{retakes.length}</p>
                  <p className={classes.statLabel}>Retakes Flagged</p>
                </div>
              </div>
            </GridItem>
          )}

          <GridItem xs={12} style={{ marginTop: "20px" }}>
            <Card className={[classes.datesCard]}>
              <CardBody>
                <h4 className={classes.datesTitle}>Upcoming Dates</h4>
                {upcomingDates.length === 0 ? (
                  <p style={{ color: "#64748b", fontSize: "14px" }}>No upcoming dates announced yet.</p>
                ) : (
                  upcomingDates.map(ev => (
                    <div key={ev.id} className={classes.dateRow}>
                      <div className={classes.dateBadge}><EventIcon /></div>
                      <div>
                        <p className={classes.dateEventTitle}>{ev.title}</p>
                        <p className={classes.dateEventWhen}>
                          {EVENT_TYPE_LABELS[ev.type] || ev.type} · {ev.date ? new Date(ev.date).toLocaleDateString() : "—"}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      </div>
    );
  }
}

PortalDashboardInner.propTypes = { classes: PropTypes.object.isRequired };
export default withStyles(styles)(PortalDashboardInner);
